import mapboxgl from 'mapbox-gl';
import { MAP_CENTER, MAP_ZOOM, MAP_MIN_ZOOM, MAP_MAX_ZOOM, MAP_STYLE, CLUSTER_RADIUS, CLUSTER_MAX_ZOOM } from '../utils/constants.js';
import { getCategoryColor } from '../utils/categories.js';
import { reportsToGeoJSON } from '../utils/helpers.js';
import { createPopupHTML, attachPopupHandlers } from './reportPopup.js';

let map = null;
let currentPopup = null;
let heatmapVisible = false;

/**
 * Initialize the Mapbox map.
 */
export function initMap(token, onMapClick) {
  mapboxgl.accessToken = token;

  map = new mapboxgl.Map({
    container: 'map',
    style: MAP_STYLE,
    center: MAP_CENTER,
    zoom: MAP_ZOOM,
    minZoom: MAP_MIN_ZOOM,
    maxZoom: MAP_MAX_ZOOM,
    attributionControl: false,
  });

  map.addControl(new mapboxgl.NavigationControl(), 'bottom-right');
  map.addControl(new mapboxgl.GeolocateControl({
    positionOptions: { enableHighAccuracy: true },
    trackUserLocation: false,
  }), 'bottom-right');

  map.on('load', () => {
    // Add empty GeoJSON source
    map.addSource('reports', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
      cluster: true,
      clusterRadius: CLUSTER_RADIUS,
      clusterMaxZoom: CLUSTER_MAX_ZOOM,
    });

    // Cluster circles
    map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'reports',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step', ['get', 'point_count'],
          '#6366f1', 10,
          '#8b5cf6', 25,
          '#a78bfa',
        ],
        'circle-radius': ['step', ['get', 'point_count'], 18, 10, 24, 25, 32],
        'circle-stroke-width': 2,
        'circle-stroke-color': 'rgba(99, 102, 241, 0.3)',
      },
    });

    // Cluster count labels
    map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'reports',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 13,
      },
      paint: { 'text-color': '#ffffff' },
    });

    // Individual report markers
    map.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: 'reports',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': [
          'match', ['get', 'category'],
          'roads', '#f97316',
          'trash', '#22c55e',
          'safety', '#ef4444',
          'lighting', '#eab308',
          'pollution', '#8b5cf6',
          'noise', '#ec4899',
          'transport', '#06b6d4',
          'other', '#64748b',
          '#6366f1',
        ],
        'circle-radius': 8,
        'circle-stroke-width': 2.5,
        'circle-stroke-color': 'rgba(255, 255, 255, 0.25)',
      },
    });

    // Heatmap layer (hidden by default)
    map.addLayer({
      id: 'heatmap',
      type: 'heatmap',
      source: 'reports',
      maxzoom: 18,
      layout: { visibility: 'none' },
      paint: {
        // Increase weight based on upvotes, but always have a baseline
        'heatmap-weight': ['interpolate', ['linear'], ['get', 'upvotes'], 0, 0.2, 50, 1],
        // Increase intensity as we zoom in
        'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 0, 1, 9, 3],
        // Vibrant glow colors
        'heatmap-color': [
          'interpolate', ['linear'], ['heatmap-density'],
          0, 'rgba(0,0,0,0)',
          0.1, 'rgba(63, 103, 241, 0.2)',
          0.3, 'rgba(139, 92, 246, 0.4)',
          0.5, 'rgba(236, 72, 153, 0.6)',
          0.7, 'rgba(249, 115, 22, 0.8)',
          1, 'rgba(239, 68, 68, 0.95)',
        ],
        // Large radius for "glow" effect
        'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 15, 9, 35, 15, 60],
        'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 7, 1, 18, 0.6],
      },
    }, 'clusters');

    // Hide loading overlay
    const loadingEl = document.getElementById('loading-overlay');
    if (loadingEl) loadingEl.classList.add('hidden');
  });

  // Click on cluster → zoom in
  map.on('click', 'clusters', (e) => {
    const features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
    const clusterId = features[0].properties.cluster_id;
    map.getSource('reports').getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err) return;
      map.easeTo({ center: features[0].geometry.coordinates, zoom });
    });
  });

  // Click on individual point → popup
  map.on('click', 'unclustered-point', (e) => {
    const feature = e.features[0];
    const coords = feature.geometry.coordinates.slice();
    const props = feature.properties;

    // Parse any stringified JSON
    const report = { ...props };
    if (typeof report.upvotes === 'string') report.upvotes = parseInt(report.upvotes, 10);

    if (currentPopup) currentPopup.remove();

    currentPopup = new mapboxgl.Popup({ offset: 15, maxWidth: '300px' })
      .setLngLat(coords)
      .setHTML(createPopupHTML(report))
      .addTo(map);

    attachPopupHandlers(currentPopup);
    e.originalEvent.stopPropagation();
  });

  // Click on empty map → place report
  map.on('click', (e) => {
    // Only if not clicking on a feature
    const features = map.queryRenderedFeatures(e.point, {
      layers: ['clusters', 'unclustered-point'],
    });
    if (features.length > 0) return;

    if (onMapClick) onMapClick({ lat: e.lngLat.lat, lng: e.lngLat.lng });
  });

  // Cursor changes
  map.on('mouseenter', 'clusters', () => { map.getCanvas().style.cursor = 'pointer'; });
  map.on('mouseleave', 'clusters', () => { map.getCanvas().style.cursor = ''; });
  map.on('mouseenter', 'unclustered-point', () => { map.getCanvas().style.cursor = 'pointer'; });
  map.on('mouseleave', 'unclustered-point', () => { map.getCanvas().style.cursor = ''; });

  return map;
}

/**
 * Update the reports data on the map.
 */
export function updateMapData(reports) {
  if (!map || !map.getSource('reports')) return;
  map.getSource('reports').setData(reportsToGeoJSON(reports));
}

/**
 * Toggle the heatmap layer visibility.
 */
export function toggleHeatmap() {
  if (!map) return false;
  heatmapVisible = !heatmapVisible;
  
  const visibility = heatmapVisible ? 'visible' : 'none';
  const otherVisibility = heatmapVisible ? 'none' : 'visible';

  map.setLayoutProperty('heatmap', 'visibility', visibility);
  
  // Hide/Show clusters and points when heatmap is on
  map.setLayoutProperty('clusters', 'visibility', otherVisibility);
  map.setLayoutProperty('cluster-count', 'visibility', otherVisibility);
  map.setLayoutProperty('unclustered-point', 'visibility', otherVisibility);

  return heatmapVisible;
}

/**
 * Fly to a specific report on the map.
 */
export function flyToReport(report) {
  if (!map) return;
  map.flyTo({ center: [report.longitude, report.latitude], zoom: 16, duration: 1200 });

  // Open popup
  setTimeout(() => {
    if (currentPopup) currentPopup.remove();
    currentPopup = new mapboxgl.Popup({ offset: 15, maxWidth: '300px' })
      .setLngLat([report.longitude, report.latitude])
      .setHTML(createPopupHTML(report))
      .addTo(map);
    attachPopupHandlers(currentPopup);
  }, 800);
}

/**
 * Enable crosshair cursor for report placement.
 */
export function setCrosshairCursor(enabled) {
  if (!map) return;
  const canvas = map.getCanvas();
  if (enabled) canvas.classList.add('crosshair-cursor');
  else canvas.classList.remove('crosshair-cursor');
}

export function getMap() { return map; }
